'use client';

import { useMemo } from 'react';

import { SystemPageType } from '@ravago/shared/page-data/enums/system-page-type';
import { UnitPrice } from '@ravago/shared/page-data/models/elements';
import { SystemPageLink } from '@ravago/shared/page-elements-radiance/components/content/system-page-link/system-page-link';
import { groupQuotationLines } from '@ravago/shared/page-elements-radiance/utils/my-products-and-prices/my-products-and-prices.utils';
import { Avatar } from '@ravago/shared/radiance/components/avatar/components/Avatar/Avatar';
import { Badge } from '@ravago/shared/radiance/components/badge/components/Badge/Badge';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { BoxItem } from '@ravago/shared/radiance/components/box/components/BoxItem/BoxItem';
import { BreakpointSwitch } from '@ravago/shared/radiance/components/breakpoint-switch/components/BreakpointSwitch/BreakpointSwitch';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { Divider } from '@ravago/shared/radiance/components/divider/components/Divider/Divider';
import { Grid } from '@ravago/shared/radiance/components/grid/components/Grid/Grid';
import { GridCol } from '@ravago/shared/radiance/components/grid/components/GridCol/GridCol';
import { Icon } from '@ravago/shared/radiance/components/icon/components/Icon/Icon';
import { Link } from '@ravago/shared/radiance/components/link/components/Link/Link';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

import { parseAsArrayOf, parseAsString, useQueryState } from 'nuqs';

import { usePathLocale } from '../../../../hooks/use-path-locale';
import { DynamicPageLink } from '../../dynamic-page-link/dynamic-page-link';
import { ProductPriceCardItem } from '../models/product-price-card-item.model';

import { ProductPriceCardV2Quotation } from './product-price-card-v2-quotation';

interface Props {
   productPriceCardItem: ProductPriceCardItem;
   productPriceCardItemId: string;
   productDetailBaseRoute: string;
   quoteDetailBaseRoute: string | null;
   limitedProductDetailBaseRoute: string | null;
   tdsAnchor: string | undefined;
   channel: string;
   canViewPrices: boolean;
   showOfflinePrices: boolean;
}

const ProductCardItem = ({ label, value }: { label: string; value: string | undefined }) => (
   <BoxItem grow={1}>
      <Box direction="column" gap="none">
         <Typography variant="small" color="subtle">
            {label}
         </Typography>
         <Typography variant="small">{value ?? '-'}</Typography>
      </Box>
   </BoxItem>
);

interface PriceLineProps {
   canViewPrices: boolean;
   smallLotDiscountedUnitPrice: UnitPrice | undefined;
   smallLotListUnitPrice: UnitPrice | undefined;
   lowestUnitPrice: UnitPrice | undefined;
   isPriceFixed: boolean | undefined;
   lowestOfflineUnitPrice: UnitPrice | undefined;
   showOfflinePrices: boolean;
   startingFromLabel: string;
   toBeConfirmedLabel: string;
   formatUnitPrice: (price: UnitPrice) => string;
   inline?: boolean;
}

const PriceLine = ({
   canViewPrices,
   smallLotDiscountedUnitPrice,
   smallLotListUnitPrice,
   lowestUnitPrice,
   isPriceFixed,
   lowestOfflineUnitPrice,
   showOfflinePrices,
   startingFromLabel,
   toBeConfirmedLabel,
   formatUnitPrice,
   inline = false
}: PriceLineProps) => {
   if (!canViewPrices) return null;

   if (smallLotDiscountedUnitPrice) {
      return (
         <Box direction={inline ? 'row' : 'column'} gap="xs" items={inline ? 'center' : 'end'}>
            <Typography variant="small" color="subtle">
               {startingFromLabel}
            </Typography>
            <Box gap="xs" items="center">
               <Typography variant="body-highlight">{formatUnitPrice(smallLotDiscountedUnitPrice)}</Typography>
               {smallLotListUnitPrice && (
                  <Typography variant="small" color="error" lineThrough>
                     {formatUnitPrice(smallLotListUnitPrice)}
                  </Typography>
               )}
            </Box>
         </Box>
      );
   }

   if (lowestUnitPrice) {
      return (
         <Box direction={inline ? 'row' : 'column'} gap="xs" items={inline ? 'center' : 'end'}>
            <Typography variant="small" color="subtle">
               {startingFromLabel}
            </Typography>
            <Box gap="xs" items="center">
               <Typography variant="body-highlight">
                  {formatUnitPrice(lowestUnitPrice)}
                  {isPriceFixed === false && '*'}
               </Typography>
               {showOfflinePrices && lowestOfflineUnitPrice && (
                  <Typography variant="small" color="subtle" lineThrough>
                     {formatUnitPrice(lowestOfflineUnitPrice)}
                  </Typography>
               )}
            </Box>
            {isPriceFixed === false && (
               <Typography variant="caption" color="subtle">
                  *{toBeConfirmedLabel}
               </Typography>
            )}
         </Box>
      );
   }

   return null;
};

interface BadgesProps {
   discountBadgeLabel: string | null;
   quoteBadgeLabel: string | null;
}

const Badges = ({ discountBadgeLabel, quoteBadgeLabel }: BadgesProps) => (
   <>
      {discountBadgeLabel && <Badge variant="info">{discountBadgeLabel}</Badge>}
      {quoteBadgeLabel && <Badge variant="info">{quoteBadgeLabel}</Badge>}
   </>
);

const addProductColorParams = (params: URLSearchParams, productPriceCardItem: ProductPriceCardItem, includeColor = false) => {
   if (productPriceCardItem.colorId != null) params.set('colorId', String(productPriceCardItem.colorId));
   if (includeColor && productPriceCardItem.color) params.set('color', productPriceCardItem.color);
   if (productPriceCardItem.producerColorCode) params.set('colorCode', productPriceCardItem.producerColorCode);
};

const createProductDetailQuery = (productPriceCardItem: ProductPriceCardItem, hasSmallLot: boolean, hasQuotes: boolean): string => {
   const params = new URLSearchParams();

   if (hasSmallLot) {
      params.set('from', 'small-lot');
      addProductColorParams(params, productPriceCardItem);
   } else if (hasQuotes) {
      params.set('from', 'quote');
      addProductColorParams(params, productPriceCardItem);
   } else {
      params.set('from', 'rfq');
      addProductColorParams(params, productPriceCardItem, true);
   }

   return params.toString();
};

export const ProductPriceCardV2 = ({
   productPriceCardItem,
   productPriceCardItemId,
   productDetailBaseRoute,
   quoteDetailBaseRoute,
   limitedProductDetailBaseRoute,
   tdsAnchor,
   channel,
   canViewPrices,
   showOfflinePrices
}: Props) => {
   const locale = usePathLocale();
   const translate = useTranslations();
   const format = useFormatter();

   const [expandedIds, setExpandedIds] = useQueryState('expandedIds', parseAsArrayOf(parseAsString).withDefault([]));
   const expanded = expandedIds.includes(productPriceCardItemId);

   const toggleExpanded = () => {
      setExpandedIds((curr) => {
         if (!curr.includes(productPriceCardItemId)) return [...curr, productPriceCardItemId];
         return curr.filter((id) => id !== productPriceCardItemId);
      });
   };

   const quotations = useMemo(
      () => groupQuotationLines(productPriceCardItem.quotationLines, productPriceCardItem.packaging),
      [productPriceCardItem.quotationLines, productPriceCardItem.packaging]
   );
   const quoteCount = quotations.length;
   const hasQuotes = quoteCount > 0;

   const smallLot = productPriceCardItem.smallLotPricing;
   const hasSmallLot = !!smallLot;

   const quoteBadgeLabel = useMemo(() => {
      if (quoteCount === 1) return translate('prices.quote-available');
      if (quoteCount > 1) return translate('prices.quotes-available');
      return null;
   }, [quoteCount, translate]);

   const discountBadgeLabel = useMemo(
      () => (smallLot ? translate('prices.online-discount-badge', { percentage: Math.round(smallLot.lowestTier.discountPercentage) }) : null),
      [smallLot, translate]
   );

   const ctaLabel = hasQuotes || hasSmallLot ? translate('prices.view-prices') : translate('prices.request-quote');

   const productDetailQuery = useMemo(
      () => createProductDetailQuery(productPriceCardItem, hasSmallLot, hasQuotes),
      [productPriceCardItem, hasSmallLot, hasQuotes]
   );
   const pdpSlugWithQuery = productPriceCardItem.slug ? `${productPriceCardItem.slug}?${productDetailQuery}` : undefined;
   const limitedProductSlugWithQuery = `${productPriceCardItem.productId}?${productDetailQuery}`;

   const pdpBaseRoute = { url: productDetailBaseRoute, noFollow: true };

   const smallLotDiscountedUnitPrice = useMemo(
      () => (smallLot ? { price: smallLot.lowestTier.discountedPrice, unitOfMeasure: smallLot.packaging.unitOfMeasure } : undefined),
      [smallLot]
   );
   const smallLotListUnitPrice = useMemo(
      () => (smallLot ? { price: smallLot.lowestTier.listPrice, unitOfMeasure: smallLot.packaging.unitOfMeasure } : undefined),
      [smallLot]
   );

   const startingFromLabel = translate('prices.starting-from');
   const toBeConfirmedLabel = translate('commodity-pricing.to-be-confirmed');
   const formatUnitPrice = (p: UnitPrice) => format.unitPrice(p);

   const hasBadges = discountBadgeLabel || quoteBadgeLabel;

   return (
      <Box direction="column" spacing="lg" borderRadius="largest" border background="white">
         <BreakpointSwitch includeViews={['phone']}>
            {hasBadges && (
               <Box justify="end" gap="2xs">
                  <Badges discountBadgeLabel={discountBadgeLabel} quoteBadgeLabel={quoteBadgeLabel} />
               </Box>
            )}
         </BreakpointSwitch>

         <Box direction="row" justify="between" gap="xl">
            <BoxItem grow={1}>
               <Box direction="column" gap="md">
                  <Box direction="row" items="start">
                     <Avatar shape="square" src={productPriceCardItem.logoUrl} />
                     <Box direction="column" gap="none">
                        {pdpSlugWithQuery && productPriceCardItem.slug ? (
                           <DynamicPageLink id={productPriceCardItem.slug} baseRoute={pdpBaseRoute} locale={locale} slug={pdpSlugWithQuery}>
                              <Typography variant="body-highlight" color="inherit">
                                 {productPriceCardItem.description}
                              </Typography>
                           </DynamicPageLink>
                        ) : (
                           <SystemPageLink
                              baseRoute={limitedProductDetailBaseRoute}
                              locale={locale}
                              type={SystemPageType.LIMITED_PRODUCT_DETAIL}
                              slug={limitedProductSlugWithQuery}
                           >
                              <Typography variant="body-highlight" color="inherit" underline>
                                 {productPriceCardItem.description}
                              </Typography>
                           </SystemPageLink>
                        )}
                        {productPriceCardItem.polymerType && (
                           <Typography variant="small" color="subtle">
                              {productPriceCardItem.polymerType}
                           </Typography>
                        )}
                     </Box>
                  </Box>

                  <Box direction="column" spacing="sm" borderRadius="largest" border>
                     <Box direction="row" gap="sm">
                        <ProductCardItem
                           label={translate('product.card.last-ordered')}
                           value={productPriceCardItem.lastOrderedOn ? format.dateTime(productPriceCardItem.lastOrderedOn) : undefined}
                        />
                        <Divider orientation="vertical" selfStretch />
                        <ProductCardItem
                           label={translate('product.card.product-ref')}
                           value={
                              productPriceCardItem.customerReferences && productPriceCardItem.customerReferences.length > 0
                                 ? productPriceCardItem.customerReferences.join(' \u2022 ')
                                 : undefined
                           }
                        />
                        <Divider orientation="vertical" selfStretch />
                        <ProductCardItem
                           label={translate('product.card.customer-ref', { customer: channel ?? '' })}
                           value={productPriceCardItem.businessContextReference?.toString()}
                        />
                     </Box>
                  </Box>

                  <Box gap="sm">
                     {productPriceCardItem.slug && (
                        <DynamicPageLink
                           id={productPriceCardItem.slug}
                           baseRoute={pdpBaseRoute}
                           locale={locale}
                           slug={productPriceCardItem.slug}
                           anchor={tdsAnchor}
                        >
                           <Box gap="xs" items="center">
                              <Icon icon="file-alt" color="inherit" size="small" />
                              <Typography color="primary" underline variant="small">
                                 {translate('prices.related-documents')}
                              </Typography>
                           </Box>
                        </DynamicPageLink>
                     )}
                     {productPriceCardItem.slug && hasQuotes && <Divider orientation="vertical" selfStretch />}
                     {hasQuotes && (
                        <Link component="button" onClick={toggleExpanded} underline="none">
                           <Box gap="2xs" items="center">
                              <Typography color="primary" underline variant="small">
                                 {translate('prices.quote-details')}
                              </Typography>
                              <Icon icon={expanded ? 'chevron-up' : 'chevron-down'} color="inherit" size="small" />
                           </Box>
                        </Link>
                     )}
                  </Box>

                  <BreakpointSwitch includeViews={['phone']}>
                     <Box direction="column" gap="md">
                        {hasQuotes && expanded && (
                           <Grid>
                              {quotations.map((quotation) => (
                                 <GridCol key={quotation.id} spanDesktop={6} spanTablet={12} spanPhone={12}>
                                    <ProductPriceCardV2Quotation quotation={quotation} quoteDetailBaseRoute={quoteDetailBaseRoute} />
                                 </GridCol>
                              ))}
                           </Grid>
                        )}
                        <Divider />
                        <Box direction="column">
                           <PriceLine
                              inline
                              canViewPrices={canViewPrices}
                              smallLotDiscountedUnitPrice={smallLotDiscountedUnitPrice}
                              smallLotListUnitPrice={smallLotListUnitPrice}
                              lowestUnitPrice={productPriceCardItem.lowestUnitPrice}
                              isPriceFixed={smallLot ? undefined : productPriceCardItem.isPriceFixed}
                              lowestOfflineUnitPrice={productPriceCardItem.lowestOfflineUnitPrice}
                              showOfflinePrices={showOfflinePrices}
                              startingFromLabel={startingFromLabel}
                              toBeConfirmedLabel={toBeConfirmedLabel}
                              formatUnitPrice={formatUnitPrice}
                           />
                        </Box>
                        {pdpSlugWithQuery && productPriceCardItem.slug ? (
                           <DynamicPageLink
                              id={productPriceCardItem.slug}
                              baseRoute={pdpBaseRoute}
                              locale={locale}
                              slug={pdpSlugWithQuery}
                              underline="none"
                              target="_blank"
                           >
                              <Button variant="primary" trailingIcon="chevron-right" fullWidth>
                                 {ctaLabel}
                              </Button>
                           </DynamicPageLink>
                        ) : (
                           <SystemPageLink
                              baseRoute={limitedProductDetailBaseRoute}
                              locale={locale}
                              type={SystemPageType.LIMITED_PRODUCT_DETAIL}
                              slug={limitedProductSlugWithQuery}
                              underline="none"
                              target="_blank"
                           >
                              <Button variant="primary" trailingIcon="chevron-right" fullWidth>
                                 {ctaLabel}
                              </Button>
                           </SystemPageLink>
                        )}
                     </Box>
                  </BreakpointSwitch>
               </Box>
            </BoxItem>

            <BreakpointSwitch excludeViews={['phone']}>
               <Divider orientation="vertical" selfStretch />
               <Box direction="column" justify="center">
                  <BoxItem>
                     <Box direction="column" items="end" justify="between" height="100%">
                        <Box gap="2xs">
                           <Badges discountBadgeLabel={discountBadgeLabel} quoteBadgeLabel={quoteBadgeLabel} />
                        </Box>
                        <PriceLine
                           canViewPrices={canViewPrices}
                           smallLotDiscountedUnitPrice={smallLotDiscountedUnitPrice}
                           smallLotListUnitPrice={smallLotListUnitPrice}
                           lowestUnitPrice={productPriceCardItem.lowestUnitPrice}
                           isPriceFixed={smallLot ? undefined : productPriceCardItem.isPriceFixed}
                           lowestOfflineUnitPrice={productPriceCardItem.lowestOfflineUnitPrice}
                           showOfflinePrices={showOfflinePrices}
                           startingFromLabel={startingFromLabel}
                           toBeConfirmedLabel={toBeConfirmedLabel}
                           formatUnitPrice={formatUnitPrice}
                        />
                        {pdpSlugWithQuery && productPriceCardItem.slug ? (
                           <DynamicPageLink
                              id={productPriceCardItem.slug}
                              baseRoute={pdpBaseRoute}
                              locale={locale}
                              slug={pdpSlugWithQuery}
                              underline="none"
                              target="_blank"
                           >
                              <Button variant="primary" trailingIcon="chevron-right" fullWidth>
                                 {ctaLabel}
                              </Button>
                           </DynamicPageLink>
                        ) : (
                           <SystemPageLink
                              baseRoute={limitedProductDetailBaseRoute}
                              locale={locale}
                              type={SystemPageType.LIMITED_PRODUCT_DETAIL}
                              slug={limitedProductSlugWithQuery}
                              underline="none"
                              target="_blank"
                           >
                              <Button variant="primary" trailingIcon="chevron-right" fullWidth>
                                 {ctaLabel}
                              </Button>
                           </SystemPageLink>
                        )}
                     </Box>
                  </BoxItem>
               </Box>
            </BreakpointSwitch>
         </Box>

         {hasQuotes && expanded && (
            <BreakpointSwitch excludeViews={['phone']}>
               <Grid>
                  {quotations.map((quotation) => (
                     <GridCol key={quotation.id} spanDesktop={6} spanTablet={12} spanPhone={12}>
                        <ProductPriceCardV2Quotation quotation={quotation} quoteDetailBaseRoute={quoteDetailBaseRoute} />
                     </GridCol>
                  ))}
               </Grid>
            </BreakpointSwitch>
         )}
      </Box>
   );
};
